import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { InkBlock } from '@/components/atoms';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { dwrClassesById } from '@/data/classes';
import { findMoveById, findMoveByIdGlobal, movesByClass } from '@/data/moves';
import type { MoveRef } from '@/schemas/character';
import type { ClassDefinition, StartingMoveChoiceGroup } from '@/schemas/classDefinition';
import type { ClassId } from '@/schemas/classId';
import { hasMoveSetup, type Move } from '@/schemas/move';
import { useBuilderDispatch, useBuilderDraft } from '../contexts/useBuilder';
import { useExpandedState } from '@/hooks/useExpandedState';
import { isMoveSetupComplete } from '../services/moveSetupComplete';
import { MoveCard } from '@/components/molecules/MoveCard';
import { MoveSetupPanel } from './MoveSetupPanel';
import { useMoveSetupState } from './useMoveSetupState';
import { CrossClassGrantedSection } from './CrossClassGrantedSection';

/**
 * Step 8 — Mosse iniziali.
 *
 * Mostra le `startingMoves` automatiche (read-only) + per ogni
 * `startingChoiceGroup` un controllo (radio se `pickCount: 1`, checkbox-cap
 * altrimenti). Per le mosse con setup strutturato (FI-18:
 * `choices`/`choiceGroups`/`narrativeFields`) viene mostrato
 * {@link MoveSetupEditor} con radio/checkbox per le scelte + input/textarea
 * per i campi narrativi.
 *
 * Include anche la **mossa concessa dall'archetipo** (`grantedMoveId`) come
 * mossa automatica del PG di livello 1.
 *
 * **Espansione descrizioni** (FI review): ogni mossa ha un toggle che
 * mostra la `description` completa inline; bottoni "Espandi tutte" /
 * "Comprimi tutte" globali per il confronto rapido.
 */
export function SectionStartingMoves() {
  const draft = useBuilderDraft();
  const classDef = draft.classId ? dwrClassesById[draft.classId] : undefined;
  if (!classDef) {
    return (
      <p className="text-sm text-muted-foreground">Seleziona prima una classe nello step 1.</p>
    );
  }
  return <StartingMovesContent classDef={classDef} />;
}

interface StartingMovesContentProps {
  classDef: ClassDefinition;
}

function StartingMovesContent({ classDef }: StartingMovesContentProps) {
  const draft = useBuilderDraft();
  const dispatch = useBuilderDispatch();

  const [groupSelections, setGroupSelections] = useState<Record<string, string[]>>(() =>
    inferGroupSelectionsFromMoves(draft.moves, classDef.startingChoiceGroups),
  );
  // Nota: l'inferenza iniziale usa solo `startingChoiceGroups` reali; i
  // virtual groups (FI-82) sono inferiti dinamicamente nel `useEffect`
  // sotto al primo render dato che `automaticMoves` può richiedere un
  // archetype dispatch.
  // **FI-81 #1 (2026-05-24)**: `MoveRef` cross-class accumulati da
  // CrossClassGrantedSection (es. warlock.vincolo-* concesso da
  // paladino.ordine-sovrannaturale). Vivono fuori dal `useMoveSetupState`
  // intra-class.
  const [crossClassRefs, setCrossClassRefs] = useState<readonly MoveRef[]>([]);

  const automaticMoves = useMemo<readonly string[]>(() => {
    const granted: string[] = [];
    if (draft.archetypeId) {
      const arch = classDef.archetypes.find((a) => a.id === draft.archetypeId);
      if (arch) granted.push(arch.grantedMoveId);
    }
    return [...classDef.startingMoves, ...granted];
  }, [classDef, draft.archetypeId]);

  // **FI-82**: virtual choice groups derivati da automaticMoves con
  // `grantsMoveChoice from-list` (vedi `deriveVirtualGroupsFromGrants`).
  const allChoiceGroups = useMemo<readonly StartingMoveChoiceGroup[]>(
    () => [...classDef.startingChoiceGroups, ...deriveVirtualGroupsFromGrants(automaticMoves)],
    [classDef.startingChoiceGroups, automaticMoves],
  );

  // Lista delle mosse correnti (auto + scelte dai gruppi), passata al hook
  // di setup per consentire l'auto-add delle opzioni `freeForArchetype`
  // (FI-32) la prima volta che il gruppo viene visualizzato.
  const selectedMoveIds = useMemo<readonly string[]>(
    () => [...automaticMoves, ...Object.values(groupSelections).flat()],
    [automaticMoves, groupSelections],
  );

  const setupState = useMoveSetupState(
    draft.moves,
    classDef.id,
    draft.archetypeId,
    selectedMoveIds,
  );
  const { choiceGroupsByMove, narrativeFieldsByMove, recruitOutcomeByMove } = setupState;

  const computedMoves = useMemo<MoveRef[]>(() => {
    const intraClass = selectedMoveIds.map((id) =>
      buildMoveRef(
        id,
        classDef.id,
        choiceGroupsByMove[id] ?? {},
        narrativeFieldsByMove[id],
        recruitOutcomeByMove[id],
      ),
    );
    // **FI-81 #1**: append i `MoveRef` cross-class concessi via
    // `grantsMoveChoice from-choice-mapping`, dedupando per id.
    const intraIds = new Set(intraClass.map((m) => m.id));
    const crossClassDeduped = crossClassRefs.filter((m) => !intraIds.has(m.id));
    return [...intraClass, ...crossClassDeduped];
  }, [
    classDef.id,
    selectedMoveIds,
    choiceGroupsByMove,
    narrativeFieldsByMove,
    recruitOutcomeByMove,
    crossClassRefs,
  ]);

  const setupCompleteByMoveId = useSetupCompleteByMoveId(
    classDef.id,
    computedMoves,
    draft.archetypeId,
  );

  useEffect(() => {
    dispatch({ type: 'SET_MOVES', moves: computedMoves });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [computedMoves]);

  const allVisibleMoveIds = useMemo<string[]>(() => {
    const fromGroups = allChoiceGroups.flatMap((g) => g.moveOptions);
    return [...automaticMoves, ...fromGroups];
  }, [allChoiceGroups, automaticMoves]);

  const expandState = useExpandedState(allVisibleMoveIds);
  // FI-79: passa gli ID delle mosse del PG per disabilitare spell-choices
  // con `requiresMove` non soddisfatto (es. Mago-Umano + Curare Ferite
  // Moderate richiede Primo Soccorso Migliorato L6-10, non disponibile a L1).
  const ownedMoveIds = useMemo(() => draft.moves.map((m) => m.id), [draft.moves]);
  const renderSetupSlot = useStartingMoveSetupSlot(setupState, draft.archetypeId, ownedMoveIds);

  return (
    <div className="flex flex-col gap-5">
      <ExpandAllToolbar expandState={expandState} />
      <AutomaticMovesList
        classId={classDef.id}
        moveIds={automaticMoves}
        expanded={expandState.expanded}
        onToggle={expandState.toggleExpanded}
        setupCompleteByMoveId={setupCompleteByMoveId}
        renderSetupSlot={renderSetupSlot}
      />
      {allChoiceGroups.map((group) => (
        <ChoiceGroupControl
          key={group.id}
          group={group}
          classId={classDef.id}
          selected={groupSelections[group.id] ?? []}
          onChange={(next) => setGroupSelections((s) => ({ ...s, [group.id]: next }))}
          expanded={expandState.expanded}
          onToggle={expandState.toggleExpanded}
          setupCompleteByMoveId={setupCompleteByMoveId}
          renderSetupSlot={renderSetupSlot}
        />
      ))}
      <CrossClassGrantedSection
        intraClassMoves={computedMoves.filter((m) => !crossClassRefs.some((c) => c.id === m.id))}
        archetypeId={draft.archetypeId}
        onCrossClassRefsChange={setCrossClassRefs}
      />
    </div>
  );
}

function ExpandAllToolbar({ expandState }: { expandState: ReturnType<typeof useExpandedState> }) {
  const { allExpanded, expandAll, collapseAll } = expandState;
  return (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={allExpanded ? collapseAll : expandAll}
        className="font-display border-ink-soft text-ink hover:bg-paper-soft border bg-transparent px-3 py-1.5 text-xs tracking-[0.08em] uppercase transition"
      >
        {allExpanded ? 'Comprimi tutte' : 'Espandi tutte'}
      </button>
    </div>
  );
}

interface MoveListProps {
  classId: ClassId;
  moveIds: readonly string[];
  expanded: Set<string>;
  onToggle: (moveId: string) => void;
  setupCompleteByMoveId: Record<string, boolean>;
  renderSetupSlot: (move: Move | undefined) => ReactNode;
}

function AutomaticMovesList({
  classId,
  moveIds,
  expanded,
  onToggle,
  setupCompleteByMoveId,
  renderSetupSlot,
}: MoveListProps) {
  if (moveIds.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <InkBlock variant="full">Mosse automatiche</InkBlock>
      <div className="flex flex-col gap-[0.6rem]">
        {moveIds.map((id) => {
          const move = findMoveById(classId, id);
          return (
            <MoveCard
              key={id}
              move={move}
              fallbackId={id}
              expanded={expanded.has(id)}
              onToggle={() => onToggle(id)}
              setupComplete={setupCompleteByMoveId[id] === true}
              setupSlot={renderSetupSlot(move)}
            />
          );
        })}
      </div>
    </div>
  );
}

interface ChoiceGroupControlProps {
  group: StartingMoveChoiceGroup;
  classId: ClassId;
  selected: string[];
  onChange: (next: string[]) => void;
  expanded: Set<string>;
  onToggle: (moveId: string) => void;
  setupCompleteByMoveId: Record<string, boolean>;
  renderSetupSlot: (move: Move | undefined) => ReactNode;
}

function ChoiceGroupControl({
  group,
  classId,
  selected,
  onChange,
  expanded,
  onToggle,
  setupCompleteByMoveId,
  renderSetupSlot,
}: ChoiceGroupControlProps) {
  if (group.pickCount === 1) {
    return (
      <div className="flex flex-col gap-2">
        <InkBlock variant="full">{group.name}</InkBlock>
        <RadioGroup
          value={selected[0] ?? ''}
          onValueChange={(v) => onChange([v])}
          className="gap-[0.6rem]"
        >
          {group.moveOptions.map((id) => {
            const inputId = `mg-${group.id}-${id}`;
            const isSelected = selected.includes(id);
            const move = findMoveById(classId, id);
            return (
              <MoveCard
                key={id}
                move={move}
                fallbackId={id}
                expanded={expanded.has(id)}
                onToggle={() => onToggle(id)}
                labelHtmlFor={inputId}
                leading={<RadioGroupItem value={id} id={inputId} />}
                selected={isSelected}
                setupComplete={isSelected && setupCompleteByMoveId[id] === true}
                setupSlot={isSelected ? renderSetupSlot(move) : null}
              />
            );
          })}
        </RadioGroup>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-2">
      <InkBlock variant="full">
        {group.name}
        <span className="text-paper-bright/70 ml-2 text-xs italic normal-case tracking-normal">
          (scegli {group.pickCount})
        </span>
      </InkBlock>
      <div className="flex flex-col gap-[0.6rem]">
        {group.moveOptions.map((id) => {
          const checked = selected.includes(id);
          const disabled = !checked && selected.length >= group.pickCount;
          const inputId = `mg-${group.id}-${id}`;
          const move = findMoveById(classId, id);
          return (
            <MoveCard
              key={id}
              move={move}
              fallbackId={id}
              expanded={expanded.has(id)}
              onToggle={() => onToggle(id)}
              labelHtmlFor={inputId}
              leading={
                <Checkbox
                  id={inputId}
                  checked={checked}
                  disabled={disabled}
                  onCheckedChange={(state) => {
                    if (state === true) onChange([...selected, id]);
                    else onChange(selected.filter((x) => x !== id));
                  }}
                />
              }
              selected={checked}
              setupComplete={checked && setupCompleteByMoveId[id] === true}
              setupSlot={checked ? renderSetupSlot(move) : null}
            />
          );
        })}
      </div>
    </div>
  );
}

/**
 * Costruisce il `MoveRef` per una mossa, proiettando lo stato locale
 * `choiceGroupsByMove[moveId]` nei campi corretti del MoveRef in base allo
 * shape della mossa nel catalogo:
 *  - mossa con `choices` shorthand → `selectedChoices` da gruppo `'main'`
 *  - mossa con `choiceGroups` multi-gruppo → `selectedChoiceGroups` da
 *    tutta la mappa (filtro out 'main' difensivo)
 *  - mossa senza setup → solo `selectedChoices: []`
 *
 * `narrativeFieldValues` è opzionale: omesso se non c'è nulla compilato
 * (rispetta `exactOptionalPropertyTypes`).
 */
function buildMoveRef(
  moveId: string,
  classId: ClassId,
  groupsForMove: Record<string, string[]>,
  narrativeForMove: Record<string, string> | undefined,
  recruitOutcome: MoveRef['recruitOutcome'],
): MoveRef {
  const move = findMoveById(classId, moveId);
  const narrativeFieldValues = narrativeForMove;
  const selectedChoices = move?.choices ? (groupsForMove.main ?? []) : [];
  const selectedChoiceGroups = move?.choiceGroups
    ? Object.fromEntries(Object.entries(groupsForMove).filter(([k]) => k !== 'main'))
    : undefined;
  return {
    id: moveId,
    selectedChoices,
    ...(selectedChoiceGroups ? { selectedChoiceGroups } : {}),
    ...(narrativeFieldValues ? { narrativeFieldValues } : {}),
    ...(recruitOutcome !== undefined ? { recruitOutcome } : {}),
  };
}

/**
 * **FI-82 (2026-05-24)** — Deriva `StartingMoveChoiceGroup` virtuali dalle
 * mosse automatiche che dichiarano `grantsMoveChoice` di kind `from-list`.
 * Caso pilota: `paladino.faro-di-devozione` → virtual group con le 2
 * opzioni Ordine. Riuso `ChoiceGroupControl` esistente: SSOT della UI di
 * scelta. ID prefisso `grant-` evita collisione con gruppi reali.
 */
function deriveVirtualGroupsFromGrants(
  automaticMoves: readonly string[],
): StartingMoveChoiceGroup[] {
  const out: StartingMoveChoiceGroup[] = [];
  for (const moveId of automaticMoves) {
    const move = findMoveByIdGlobal(moveId);
    if (!move) continue;
    const grant = move.grantsMoveChoice;
    if (grant?.kind !== 'from-list') continue;
    out.push({
      id: `grant-${move.id}`,
      name: `Concessa da «${move.name}»`,
      moveOptions: [...grant.moveOptions],
      pickCount: 1,
      expandableInAdvanced: false,
    });
  }
  return out;
}

function inferGroupSelectionsFromMoves(
  moves: readonly MoveRef[],
  groups: readonly StartingMoveChoiceGroup[],
): Record<string, string[]> {
  const result: Record<string, string[]> = {};
  const moveIds = new Set(moves.map((m) => m.id));
  for (const g of groups) {
    result[g.id] = g.moveOptions.filter((id) => moveIds.has(id));
  }
  return result;
}

/**
 * FI-UX polish: hook che calcola `setupCompleteByMoveId` per la lista di
 * mosse correnti. Estratto da `SectionStartingMoves` per rispettare
 * `max-lines-per-function: 100`. La spunta verde appare accanto al nome
 * della mossa nella lista quando tutti i campi obbligatori (choices/
 * choiceGroups con pickCount + narrativeFields required visibili) sono
 * compilati.
 *
 * Le mosse senza setup non finiscono nella mappa: il rendering della
 * spunta è già filtrato a monte (la spunta appare solo se il valore in
 * mappa è `true`).
 */
function useSetupCompleteByMoveId(
  classId: ClassId | undefined,
  computedMoves: readonly MoveRef[],
  archetypeId: string | undefined,
): Record<string, boolean> {
  return useMemo<Record<string, boolean>>(() => {
    if (!classId) return {};
    const map: Record<string, boolean> = {};
    for (const ref of computedMoves) {
      const move = findMoveById(classId, ref.id);
      if (!move || !hasMoveSetup(move)) continue;
      map[ref.id] = isMoveSetupComplete(move, ref, archetypeId);
    }
    return map;
  }, [classId, computedMoves, archetypeId]);
}

/**
 * FI-75: hook che restituisce il `renderSetupSlot` per le `MoveCard` del
 * builder L1. Estratto da `StartingMovesContent` per rispettare
 * `max-lines-per-function: 100`. Ritorna `null` se la mossa non ha setup
 * (la card resta non espandibile senza description), altrimenti renderizza
 * `<MoveSetupPanel>` con dati e callback dal `useMoveSetupState`.
 */
function useStartingMoveSetupSlot(
  setupState: ReturnType<typeof useMoveSetupState>,
  archetypeId: string | undefined,
  ownedMoveIds: readonly string[],
): (move: Move | undefined) => ReactNode {
  const { choiceGroupsByMove, narrativeFieldsByMove, recruitOutcomeByMove } = setupState;
  return useCallback(
    (move: Move | undefined): ReactNode => {
      if (!move || !hasMoveSetup(move)) return null;
      return (
        <MoveSetupPanel
          move={move}
          selectedByGroup={choiceGroupsByMove[move.id] ?? {}}
          narrativeValues={narrativeFieldsByMove[move.id] ?? {}}
          recruitOutcome={recruitOutcomeByMove[move.id]}
          archetypeId={archetypeId}
          ownedMoveIds={ownedMoveIds}
          onChoiceGroupChange={setupState.handleChoiceGroupChange}
          onNarrativeFieldChange={setupState.handleNarrativeFieldChange}
          onRecruitOutcomeChange={setupState.handleRecruitOutcomeChange}
        />
      );
    },
    [
      choiceGroupsByMove,
      narrativeFieldsByMove,
      recruitOutcomeByMove,
      archetypeId,
      ownedMoveIds,
      setupState,
    ],
  );
}

// Export inutilizzato dell'aggregato (silenzia tree-shake warning su dipendenze).
void movesByClass;
